// Ops → Automation: browse and test the conversational flows authored in Movorra ECAP.
// Read-friendly view for business users (flows, their attributes/rules/hooks, live analytics) plus an
// inline streaming tester. Talks to the ECAP service via services/ecapApi.js. Authoring/editing is a
// follow-up; today this surfaces flows + lets you chat-test them. See src/pages/README.md.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader, Button, Alert, StatusBadge } from '../ui/primitives';
import { IconRoute, IconSparkles, IconRefresh, IconBot, IconUser, IconCircleCheck, IconEdit, IconTrash } from '../ui/icons';
import { ConfirmDialog } from '../ui/Modal';
import { Rocket, ExternalLink, Copy, Power } from 'lucide-react';
import {
  listFlows, getFlow, getFlowKey, setFlowCors, getFlowAnalytics, publishFlow, simulateFlow,
  compileFlow, chatPageUrl, embedSnippet,
  getFlowDemo, deployFlowDemo, takeDownFlowDemo, setFlowType, demoPageUrl,
  getVersions, rollbackFlow, exportFlow, importFlow, deleteFlow,
  startSession, streamUrl, ecapBase, getSession, confirmPayment, getAiTraceSession,
  activeTenant, whoami, getFlowAccessLevels, setFlowAccessLevel, setFlowModel,
  getEvalCases, setEvalCases, runEval,
  uploadSessionFile, submitComponentResult, attachmentUrl,
} from '../services/ecapApi';
import QRCode from 'qrcode';
import OpsFlowCanvas from './OpsFlowCanvas';
import OpsLibrary from './OpsLibrary';

const ACCESS_LEVELS = ['EDITABLE', 'READ_ONLY', 'API_ONLY'];
const ACCESS_TONE = { READ_ONLY: 'bg-amber-50 text-amber-700', API_ONLY: 'bg-slate-200 text-slate-600' };
// Per-flow model choices (advisory; '' = platform default). Mirrors the backend llm.models catalogue.
const FLOW_MODELS = ['', 'gpt-4.1-mini', 'gpt-4.1', 'gpt-4o', 'gpt-4o-mini'];

function Chip({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-600',
    green: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${tones[tone]}`}>{children}</span>;
}

// A self-contained visual diagram of a flow: Start → collect attributes → branch → actions → Complete.
// Code-capability hooks render as distinct indigo nodes (⚙) showing the capability + what it writes.
function DiagNode({ tone = 'slate', title, children }) {
  const t = { slate: 'border-slate-200 bg-white', green: 'border-emerald-200 bg-emerald-50',
    blue: 'border-blue-200 bg-blue-50', indigo: 'border-indigo-200 bg-indigo-50' }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${t}`}>
      <div className="font-medium text-slate-800">{title}</div>
      {children}
    </div>
  );
}
function DiagArrow() {
  return <div className="flex justify-center my-1"><div className="w-px h-4 bg-slate-300" /></div>;
}
function FieldChips({ fields }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {(fields || []).map((f) => (
        <span key={f.key} className="text-[11px] px-1.5 py-0.5 rounded bg-white/70 border border-slate-200 text-slate-600">{f.label || f.key}</span>
      ))}
      {(fields || []).length === 0 && <span className="text-[11px] text-slate-300">—</span>}
    </div>
  );
}
function FlowDiagram({ flow }) {
  const cond = (flow.branches || [])[0]?.activation?.[0] || {};
  const branchAttr = cond.attribute || cond.fieldKey || cond.key || 'the answer';
  const hookTarget = (h) => (h.codeCapabilityRef
    ? <>code capability <span className="font-mono text-indigo-600">{h.codeCapabilityRef}</span></>
    : h.capabilityRef ? <>capability <span className="font-mono">{h.capabilityRef}</span></>
      : h.url ? <span className="font-mono">{h.method || 'POST'} {h.url}</span> : 'action');
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4 max-w-2xl">
      <DiagNode tone="green" title="▶ Start">
        <div className="text-xs text-slate-500 italic mt-0.5 line-clamp-2">“{flow.greeting}”</div>
      </DiagNode>
      <DiagArrow />
      <DiagNode title="Collect attributes"><FieldChips fields={flow.fields} /></DiagNode>
      {(flow.branches || []).length > 0 && (
        <>
          <DiagArrow />
          <DiagNode tone="blue" title={<span>Branch on <span className="font-mono">{branchAttr}</span></span>}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
              {flow.branches.map((b) => (
                <div key={b.id} className="rounded-md border border-blue-100 bg-white p-2">
                  <div className="text-xs font-medium text-blue-700">{b.name || b.id}</div>
                  <FieldChips fields={b.fields} />
                </div>
              ))}
            </div>
          </DiagNode>
        </>
      )}
      {(flow.hooks || []).length > 0 && (
        <>
          <DiagArrow />
          <div className="space-y-1.5">
            {flow.hooks.map((h) => (
              <DiagNode key={h.name} tone={h.codeCapabilityRef ? 'indigo' : 'slate'}
                title={<span className="flex items-center gap-2">{h.codeCapabilityRef ? '⚙' : '⇄'} {h.name}
                  <span className="text-[10px] font-normal text-slate-400">{h.trigger || 'ON_COMPLETE'}</span></span>}>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {hookTarget(h)}
                  {h.responseMapping && Object.keys(h.responseMapping).length > 0 && <> → writes {Object.keys(h.responseMapping).join(', ')}</>}
                </div>
              </DiagNode>
            ))}
          </div>
        </>
      )}
      <DiagArrow />
      <DiagNode tone="green" title="✓ Complete">
        <div className="text-xs text-slate-500 italic mt-0.5 line-clamp-2">“{flow.completionMessage}”</div>
      </DiagNode>
    </div>
  );
}

function OpsFlows({ authSession }) {
  const [flows, setFlows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [flow, setFlow] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | flow object being edited
  const [view, setView] = useState('flows'); // 'flows' | 'library'
  const [tenant] = useState(activeTenant()); // fixed per mount; the shell remounts this page on company switch
  const [access, setAccess] = useState({}); // flowId → EDITABLE|READ_ONLY|API_ONLY
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => { whoami().then((w) => setIsAdmin(!!w?.admin)).catch(() => {}); }, []);

  const loadFlows = useCallback(async () => {
    setLoading(true); setError(null); setSelectedId(null); setFlow(null);
    try {
      const [f, acc] = await Promise.all([listFlows(authSession), getFlowAccessLevels(authSession).catch(() => ({}))]);
      setFlows(f || []); setAccess(acc || {});
    } catch (e) { setError(`Couldn't load flows from ECAP (${ecapBase()}). ${e.message}`); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession, tenant]); // `tenant` re-fetches on a super-admin tenant switch (used via ecapApi headers)

  const changeAccess = async (flowId, level) => {
    try { await setFlowAccessLevel(flowId, level, authSession); setAccess((a) => ({ ...a, [flowId]: level })); }
    catch (e) { setError(e.message); }
  };

  useEffect(() => { loadFlows(); }, [loadFlows]);

  const latestSelectRef = useRef(null);
  const selectFlow = async (id) => {
    latestSelectRef.current = id;
    setSelectedId(id); setFlow(null); setAnalytics(null); setIssues(null); setDemo(null);
    try {
      const [f, a, d] = await Promise.all([
        getFlow(id, authSession),
        getFlowAnalytics(id, authSession).catch(() => null),
        getFlowDemo(id, authSession).catch(() => null),
      ]);
      // Ignore a stale response: if the operator clicked another flow while this was in flight, a slower
      // getFlow must NOT overwrite the newer selection's detail (else Edit would open the wrong flow).
      if (latestSelectRef.current !== id) return;
      setFlow(f); setAnalytics(a); setDemo(d);
      // B5: run the same check publish runs, so blockers/warnings show before Publish is hit.
      compileFlow(id, authSession)
        .then((c) => { if (latestSelectRef.current === id) setIssues(c?.issues || []); })
        .catch(() => { /* compile check is advisory here — never block the detail view */ });
    } catch (e) { if (latestSelectRef.current === id) setError(e.message); }
  };

  // B5: compile issues for the selected flow (null = not checked / check failed; [] = clean).
  const [issues, setIssues] = useState(null);
  // Deploy Demo status for the selected flow (null = not loaded; {deployed, token, pinnedVersion, enabled,
  // flowType, latestVersion} otherwise). Separate from publishing — it never reflects the live version.
  const [demo, setDemo] = useState(null);
  const [confirmTakedown, setConfirmTakedown] = useState(null);   // null | { id, name }

  const doPublish = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const r = await publishFlow(selectedId, authSession);
      if (r && r.published === false) {
        // Publish is compile-gated server-side — say WHY instead of silently not publishing.
        setIssues(r.issues || []);
        setError('Publish blocked — fix the errors listed on the flow card first.');
      } else {
        setError(null);
        await selectFlow(selectedId);
      }
    }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // Deploy (or update) the demo, then open it — the window.open must be synchronous within the click's
  // task or the browser blocks it as a popup, so it happens right here rather than in a later effect.
  const doDeployDemo = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const r = await deployFlowDemo(selectedId, authSession);
      if (r && r.deployed === false) {
        // Deploy is compile-gated server-side, exactly like Publish — say WHY rather than failing silently.
        setIssues(r.issues || []);
        setError('Demo not deployed — fix the errors listed on the flow card first.');
      } else {
        setError(null);
        if (r && r.token) window.open(demoPageUrl(r.token), '_blank', 'noopener');
        await selectFlow(selectedId);
      }
    }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const doTakeDownDemo = async () => {
    if (!selectedId) return;
    setBusy(true);
    try { await takeDownFlowDemo(selectedId, authSession); setError(null); await selectFlow(selectedId); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const changeFlowType = async (type) => {
    if (!selectedId) return;
    try { await setFlowType(selectedId, type, authSession); await selectFlow(selectedId); }
    catch (e) { setError(e.message); }
  };

  const doExport = async () => {
    if (!selectedId) return;
    try {
      const doc = await exportFlow(selectedId, authSession);
      const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${selectedId}.flow.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };

  // Delete a flow — always behind a confirm. Two stages: a normal confirm; then, if the backend refuses
  // because the flow is currently PUBLISHED/live (409, DELETE-LIVE-1), a second, explicit "delete anyway"
  // confirm that passes ?force=true. Deleting removes the draft, every version snapshot and its test cases;
  // the flow's chat key stops working.
  const [confirmDelete, setConfirmDelete] = useState(null); // null | { id, name, force: boolean }
  const doDelete = async (force) => {
    const target = confirmDelete;
    if (!target) return;
    setBusy(true); setError(null);
    try {
      await deleteFlow(target.id, authSession, force);
      setConfirmDelete(null);
      await loadFlows();
    } catch (e) {
      if (e.status === 409 && !force) {
        setConfirmDelete({ ...target, force: true }); // live flow → ask again, explicitly
      } else {
        setConfirmDelete(null);
        setError(`Delete failed: ${e.message}`);
      }
    } finally { setBusy(false); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const doc = JSON.parse(await file.text());
      const saved = await importFlow(doc.id || null, doc, authSession);
      await loadFlows();
      if (saved?.id) await selectFlow(saved.id);
    } catch (e) {
      // IMPORT-409-OPAQUE: the backend now 409s an import onto an existing flow id (import does NOT overwrite
      // unless ?overwrite). Turn that into actionable guidance instead of a raw error the author can't act on.
      if (e.status === 409) setError('A flow with that id already exists — rename it in the file, or delete the existing flow first, before importing.');
      else setError(`Import failed: ${e.message}`);
    }
    finally { setBusy(false); }
  };

  const doRollback = async (version) => {
    if (!selectedId) return;
    setBusy(true);
    try { await rollbackFlow(selectedId, version, authSession); await selectFlow(selectedId); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6">
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => doDelete(!!confirmDelete?.force)}
        variant="danger"
        title={confirmDelete?.force ? 'This flow is live — delete anyway?' : `Delete "${confirmDelete?.name || confirmDelete?.id}"?`}
        confirmLabel={confirmDelete?.force ? 'Delete anyway' : 'Delete flow'}
        message={confirmDelete?.force
          ? `"${confirmDelete?.name || confirmDelete?.id}" has a published version that customers can reach right now. Deleting it stops the live chat immediately, with no rollback. Are you sure?`
          : 'This permanently removes the flow, all of its published versions and its saved test cases. Its chat key stops working. This cannot be undone.'}
      />
      <ConfirmDialog
        open={!!confirmTakedown}
        onClose={() => setConfirmTakedown(null)}
        onConfirm={doTakeDownDemo}
        variant="danger"
        title={`Take down the demo for "${confirmTakedown?.name || confirmTakedown?.id}"?`}
        confirmLabel="Take down demo"
        message={'The demo link stops working immediately — including for anyone with it already open. '
          + 'The flow itself, its versions and its live chat are untouched. Deploying the demo again brings '
          + 'back the very same link.'}
      />
      <PageHeader
        title="Automation"
        subtitle="Conversational flows — collect, validate, and act, with no code"
        right={<div className="flex gap-2">
          <Button variant="ghost" onClick={loadFlows}><IconRefresh size={16} /> Refresh</Button>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer">
            ⬆ Import
            <input type="file" accept=".json,application/json" className="hidden" onChange={(e) => { doImport(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <Button onClick={() => { setEditing('new'); setFlow(null); setSelectedId(null); }}>+ New flow</Button>
        </div>}
      />
      {error && <Alert tone="danger" className="mb-4">{error}</Alert>}

      <div className="flex gap-1.5 mb-4 border-b border-slate-200">
        {[['flows', 'Flows'], ['library', 'Library']].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${view === k ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{l}</button>
        ))}
      </div>

      {view === 'library' && <OpsLibrary key={tenant} authSession={authSession} />}

      {view === 'flows' && (
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
        {/* Flow list */}
        <div className="space-y-2">
          {loading && <div className="text-sm text-slate-500">Loading flows…</div>}
          {!loading && flows.length === 0 && (
            <div className="text-sm text-slate-500">No flows yet. Author one in the ECAP Studio.</div>
          )}
          {flows.map((f) => (
            <button
              key={f.id}
              onClick={() => selectFlow(f.id)}
              className={`w-full text-left rounded-xl border p-3 transition ${
                selectedId === f.id ? 'border-blue-500 ring-2 ring-blue-100 bg-white' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="font-semibold text-slate-800 flex items-center gap-2">
                <IconRoute size={16} /> {f.name || f.id}
                {access[f.id] && access[f.id] !== 'EDITABLE' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${ACCESS_TONE[access[f.id]]}`}>{access[f.id] === 'API_ONLY' ? 'API-only' : 'read-only'}</span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {(f.fields || []).length} attributes · {(f.rules || []).length} rules · {(f.hooks || []).length} actions
              </div>
            </button>
          ))}
        </div>

        {/* Detail + tester (the editor opens as a full-screen overlay, below) */}
        <div>
          {!flow ? (
            <div className="text-sm text-slate-500 mt-2">Select a flow to see its details and test it, or create a new one.</div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <FlowDetail flow={flow} analytics={analytics} issues={issues} onPublish={doPublish} onEdit={() => setEditing(flow)}
                onExport={doExport} onRollback={doRollback} authSession={authSession} busy={busy}
                onDelete={() => setConfirmDelete({ id: flow.id, name: flow.name, force: false })}
                accessLevel={access[flow.id] || 'EDITABLE'} isAdmin={isAdmin} onSetAccess={(lvl) => changeAccess(flow.id, lvl)}
                demo={demo} onDeployDemo={doDeployDemo} onSetFlowType={changeFlowType}
                onTakeDownDemo={() => setConfirmTakedown({ id: flow.id, name: flow.name })}
                onModelSaved={(m) => setFlow((prev) => (prev ? { ...prev, llmModel: m } : prev))} />
              <FlowTester flowId={flow.id} authSession={authSession} />
            </div>
          )}
        </div>
      </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex flex-col p-3 sm:p-5">
          <div className="flex-1 min-h-0 max-w-[1800px] w-full mx-auto">
            <OpsFlowCanvas
              flow={editing === 'new' ? null : editing}
              authSession={authSession}
              onSaved={(saved) => { setEditing(null); loadFlows(); if (saved?.id) selectFlow(saved.id); }}
              onCancel={() => setEditing(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function FlowDetail({ flow, analytics, issues, onPublish, onEdit, onExport, onRollback, onDelete, authSession, busy, accessLevel = 'EDITABLE', isAdmin, onSetAccess, onModelSaved, demo, onDeployDemo, onTakeDownDemo, onSetFlowType }) {
  const editable = isAdmin || accessLevel === 'EDITABLE'; // super-admin can always edit/publish
  const [model, setModel] = useState(flow.llmModel || '');
  const [modelMsg, setModelMsg] = useState(false);
  const [modelErr, setModelErr] = useState(null); // F4: a failed model save was swallowed (/* surfaced elsewhere */) — surface it
  const [showDiagram, setShowDiagram] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setModel(flow.llmModel || ''); setModelErr(null); setCopied(false); }, [flow.id, flow.llmModel]);
  const live = !!(demo && demo.deployed && demo.enabled);
  const demoUrl = demo && demo.token ? demoPageUrl(demo.token) : null;
  const behind = live && demo.latestVersion != null && demo.pinnedVersion != null
    && demo.latestVersion > demo.pinnedVersion;
  const copyDemoLink = () => {
    if (!demoUrl) return;
    try {
      navigator.clipboard.writeText(demoUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) { /* clipboard unavailable — the link is visible and selectable below */ }
  };
  const saveModel = async (m) => {
    const prev = model;
    setModel(m); setModelErr(null);
    try {
      await setFlowModel(flow.id, m, authSession);
      setModelMsg(true); setTimeout(() => setModelMsg(false), 1200);
      // F2: keep the parent flow's llmModel in sync. Otherwise a later "Edit" opens the canvas with a stale
      // `flow` snapshot, and graphToFlow re-emits the OLD llmModel (read from `original`) on Save — a
      // full-replace PUT that silently overwrites the model just chosen here.
      onModelSaved?.(m);
    } catch (e) {
      setModel(prev); // revert the optimistic dropdown value so it reflects what's actually stored
      setModelErr(e?.message || 'Could not save the model.');
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      {/* Header: title left, controls right. flex-wrap + shrink-0 so the Publish button never overflows. */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-slate-800 truncate">{flow.name || flow.id}</h3>
          <div className="text-xs text-slate-500">{flow.id}{flow.version != null && <> · v{flow.version}</>}</div>
        </div>
        {/* min-w-0 (not shrink-0): the toolbar must WRAP inside the card, never push Publish off the tile. */}
        <div className="flex gap-2 items-center flex-wrap justify-end min-w-0">
          {/* Super-admin governs what a tenant user may do with this flow. */}
          {isAdmin && (
            <select className="text-xs rounded-md border border-slate-200 px-1.5 py-1" value={accessLevel}
              onChange={(e) => onSetAccess?.(e.target.value)} title="Access level for this company's users">
              {ACCESS_LEVELS.map((l) => <option key={l} value={l}>{l === 'API_ONLY' ? 'API-only' : l === 'READ_ONLY' ? 'Read-only' : 'Editable'}</option>)}
            </select>
          )}
          <Button size="sm" variant="ghost" onClick={onExport}>⬇ Export</Button>
          {editable && onDelete && (
            // Icon-only so the toolbar stays compact; the confirm dialog names the flow before anything happens.
            <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy} title="Delete this flow"
              aria-label="Delete this flow" className="text-danger hover:bg-danger-50 px-2"><IconTrash size={15} /></Button>
          )}
          {editable && <Button size="sm" variant="outline" onClick={onEdit}><IconEdit size={15} /> Edit</Button>}
          {/* Deploy Demo is deliberately its own action: it never changes what production serves. */}
          {editable && (
            <Button size="sm" variant="outlineIndigo" onClick={onDeployDemo} disabled={busy}
              title={live ? 'Re-pin the demo link to the current saved flow' : 'Put this flow on a shareable demo link'}>
              <Rocket size={15} /> {live ? 'Update Demo' : 'Deploy Demo'}
            </Button>
          )}
          {editable && <Button size="sm" onClick={onPublish} disabled={busy}><IconCircleCheck size={15} /> Publish</Button>}
          {!editable && <span className="text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded">{accessLevel === 'API_ONLY' ? 'API-only' : 'read-only'}</span>}
        </div>
      </div>

      <p className="text-sm text-slate-600 mt-3 italic">“{flow.greeting}”</p>

      {/* B5: compile check — the same validation publish runs server-side. Errors block publishing. */}
      {issues && issues.length > 0 && (
        <div className="mt-3 space-y-1">
          {issues.map((it, i) => (
            <div key={i} className={`text-xs rounded-lg px-2.5 py-1.5 border ${it.severity === 'ERROR'
              ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
              <b>{it.severity === 'ERROR' ? 'Blocks publish' : 'Warning'}</b> · {it.message}
              <span className="opacity-60"> ({it.code})</span>
            </div>
          ))}
        </div>
      )}
      {issues && issues.length === 0 && (
        <div className="mt-3 text-xs text-emerald-600">✓ Checks pass — this flow can be published.</div>
      )}

      {/* Deploy Demo — a shareable, anonymous link pinned to a snapshot. Separate from Publish: nothing here
          changes the flow's live version. Only rendered once a demo exists; before that the toolbar button
          is the entry point. */}
      {demo && demo.deployed && (
        <div className={`mt-3 rounded-lg border px-3 py-2.5 ${live ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={live ? 'active' : 'inactive'} />
            <span className="text-xs text-slate-600">
              {live ? 'Demo is live' : 'Demo taken down'}
              {demo.pinnedVersion != null && <span className="text-slate-400"> · pinned to v{demo.pinnedVersion}</span>}
            </span>
            <div className="flex gap-2.5 items-center ml-auto">
              {live && demoUrl && (
                <a className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                  href={demoUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Open Demo</a>
              )}
              {live && demoUrl && (
                <button type="button" className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                  onClick={copyDemoLink}><Copy size={13} /> {copied ? 'copied ✓' : 'Copy Link'}</button>
              )}
              {editable && live && (
                <button type="button" className="inline-flex items-center gap-1 text-[11px] text-danger hover:underline"
                  onClick={onTakeDownDemo} disabled={busy}><Power size={13} /> Take Down</button>
              )}
            </div>
          </div>
          {live && demoUrl && <div className="mt-1.5 text-[11px] font-mono text-slate-500 break-all">{demoUrl}</div>}
          {behind && (
            <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Draft has newer changes. The demo keeps showing v{demo.pinnedVersion} until you hit Update Demo —
              so a link you are presenting can&apos;t change under you.
            </div>
          )}
          {!live && (
            <div className="mt-1.5 text-[11px] text-slate-500">
              The link is revoked. Deploy Demo brings back this exact URL.
            </div>
          )}
        </div>
      )}

      {/* Per-flow AI model — overrides the platform default for this flow's extraction. */}
      {editable && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-slate-400 uppercase tracking-wide">Type</span>
          <select className="rounded-md border border-slate-200 px-2 py-1 text-sm"
            value={demo?.flowType === 'QUOTE' ? 'QUOTE' : 'GENERIC'}
            onChange={(e) => onSetFlowType?.(e.target.value)}
            title="What kind of workflow this is — decides which interface its demo link renders">
            <option value="QUOTE">Quote bot</option>
            <option value="GENERIC">Generic chat</option>
          </select>
          <span className="text-slate-400 uppercase tracking-wide ml-2">AI model</span>
          <select className="rounded-md border border-slate-200 px-2 py-1 text-sm" value={model} onChange={(e) => saveModel(e.target.value)}
            title="Model used for this flow's extraction; Platform default falls back to the global setting">
            {FLOW_MODELS.map((m) => <option key={m || 'default'} value={m}>{m || 'Platform default'}</option>)}
          </select>
          {modelMsg && <span className="text-emerald-600">saved</span>}
          {modelErr && <span className="text-rose-600" title={modelErr}>⚠ save failed — {modelErr}</span>}
        </div>
      )}

      <div className="mt-3">
        <button className="text-xs text-blue-600 hover:text-blue-800" onClick={() => setShowDiagram((v) => !v)}>
          {showDiagram ? '▾ Hide flow diagram' : '▸ Show flow diagram'}
        </button>
        {showDiagram && <div className="mt-2"><FlowDiagram flow={flow} /></div>}
      </div>

      <SectionTitle>Attributes it collects</SectionTitle>
      {/* Clean card grid — one compact card per attribute (label, tags, hint) instead of a cramped row. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(flow.fields || []).map((f) => (
          <div key={f.key} className="rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-slate-800 text-sm">{f.label || f.key}</span>
              {f.required && <Chip tone="amber">required</Chip>}
              {f.derived && <Chip tone="blue">AI-inferred</Chip>}
              {(f.dependsOn || []).length > 0 && <Chip>after {f.dependsOn.join(', ')}</Chip>}
            </div>
            {f.extractionHint && <div className="text-slate-400 text-xs mt-0.5">{f.extractionHint}</div>}
          </div>
        ))}
        {(flow.fields || []).length === 0 && <div className="text-sm text-slate-300">No attributes.</div>}
      </div>

      {(flow.hooks || []).length > 0 && (
        <>
          <SectionTitle>Actions (hooks)</SectionTitle>
          <div className="space-y-1.5">
            {flow.hooks.map((h) => (
              <div key={h.name} className="text-sm text-slate-700 flex items-center gap-2 flex-wrap">
                <IconSparkles size={14} /> <span className="font-medium">{h.name}</span>
                <Chip tone={h.trigger === 'ON_ATTRIBUTES' ? 'blue' : 'green'}>{h.trigger || 'ON_COMPLETE'}</Chip>
                {h.codeCapabilityRef
                  ? <Chip tone="indigo">⚙ code capability: {h.codeCapabilityRef}</Chip>
                  : h.capabilityRef
                    ? <Chip>capability: {h.capabilityRef}</Chip>
                    : h.url && <span className="text-[11px] text-slate-400 font-mono">{h.method || 'POST'} {h.url}</span>}
                {h.responseMapping && Object.keys(h.responseMapping).length > 0 && (
                  <span className="text-[11px] text-slate-400">→ writes {Object.keys(h.responseMapping).join(', ')}</span>
                )}
                {h.componentTemplate && <Chip>renders component</Chip>}
              </div>
            ))}
          </div>
        </>
      )}

      {analytics && (
        <>
          <SectionTitle>Performance</SectionTitle>
          <div className="grid grid-cols-3 gap-3 text-center">
            <Stat label="Sessions" value={analytics.totalSessions} />
            <Stat label="Completion" value={`${Math.round((analytics.completionRate || 0) * 100)}%`} />
            <Stat label="Avg tokens" value={Math.round(analytics.avgTokensPerSession || 0)} />
          </div>
        </>
      )}

      <EvalPanel flowId={flow.id} flow={flow} authSession={authSession} editable={editable} />
      <AiFlowTester flowId={flow.id} authSession={authSession} />
      <IntegrationSnippets flowId={flow.id} flowName={flow.name || flow.id} authSession={authSession} />
      <VersionsPanel flowId={flow.id} authSession={authSession} onRollback={onRollback} busy={busy} />
    </div>
  );
}

// AI Flow Tester — describe a customer persona; the AI role-plays them through the flow (dry-run, no
// real actions) so you see, before publishing, whether it completes or gets stuck. Unique pre-launch QA.
// (Embed-widget + runtime-API snippets now live on the dedicated Integration & API page, OpsIntegration.js.)
const PERSONA_PRESETS = [
  'a cooperative customer who answers every question clearly',
  'a rushed customer who gives vague, one-word answers',
  'an angry customer who complains and is hard to please',
  'a customer who changes their mind halfway through',
];
// Chip multiselect: toggle keys from a fixed option set (used for filled / not-filled / actions).
function ChipMulti({ options, value, onChange, disabled, tone = 'blue' }) {
  const val = value || [];
  const toggle = (o) => onChange(val.includes(o) ? val.filter((v) => v !== o) : [...val, o]);
  const on = tone === 'green' ? 'bg-emerald-600 text-white border-emerald-600' : tone === 'rose' ? 'bg-rose-500 text-white border-rose-500' : 'bg-blue-600 text-white border-blue-600';
  return (
    <div className="flex flex-wrap gap-1">
      {options.length === 0 && <span className="text-[11px] text-slate-300">none</span>}
      {options.map((o) => (
        <button key={o} type="button" disabled={disabled} onClick={() => toggle(o)}
          className={`text-[11px] px-1.5 py-0.5 rounded-full border ${val.includes(o) ? on : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{o}</button>
      ))}
    </div>
  );
}

// Eval suite: saved test scenarios + expected outcomes (values, fields filled/not-filled, branch, actions,
// completion). "Run all" drives each through the chat with actions FIRING (so a code capability runs) and
// reports pass/fail + exactly what diverged, the branch it took, the actions that fired, and the transcript.
function EvalPanel({ flowId, flow, authSession, editable }) {
  const [cases, setCases] = useState([]);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState(null);
  // eval-cases-load-error-overwrite: only true once the saved suite has actually loaded. setEvalCases is a
  // full-list REPLACE, so if the initial GET failed (transient 5xx) we must NOT let a Save/auto-save persist
  // the empty/partial local list over the server's real tests.
  const [loaded, setLoaded] = useState(false);

  // Options from the flow (attributes across base + branches, branch ids, action names).
  const attrKeys = (() => { const s = new Set(); (flow.fields || []).forEach((f) => s.add(f.key)); (flow.branches || []).forEach((b) => (b.fields || []).forEach((f) => s.add(f.key))); return [...s].filter(Boolean); })();
  const branchOpts = (flow.branches || []).map((b) => b.id).filter(Boolean);
  const actionOpts = (flow.hooks || []).map((h) => h.name).filter(Boolean);

  // Editing model: expected values are edited as rows (attribute + value), converted to a map for the API.
  const fromApi = (c) => ({ ...c, _rows: Object.entries(c.expectedValues || {}).map(([key, value]) => ({ key, value })) });
  const toApi = (cs) => cs.map(({ _rows, ...c }) => ({ ...c, expectedValues: Object.fromEntries((_rows || []).filter((r) => (r.key || '').trim()).map((r) => [r.key.trim(), r.value])) }));

  useEffect(() => {
    let alive = true;
    setLoaded(false); setError(null);
    getEvalCases(flowId, authSession)
      .then((cs) => { if (alive) { setCases((cs || []).map(fromApi)); setLoaded(true); } })
      .catch((e) => { if (alive) setError(`Could not load the saved tests (${e?.message || e}). Refresh before editing — saving now would overwrite them.`); });
    return () => { alive = false; };
  }, [flowId, authSession]);

  const upd = (i, patch) => { setCases(cases.map((c, j) => (j === i ? { ...c, ...patch } : c))); setDirty(true); };
  const del = (i) => { setCases(cases.filter((_, j) => j !== i)); setDirty(true); };
  const addCase = () => { setCases([...cases, { name: `Test ${cases.length + 1}`, scenario: '', _rows: [], expectComplete: true, messageContains: '', expectedBranch: '', expectedActions: [], expectFilled: [], expectNotFilled: [] }]); setDirty(true); };
  const addRow = (i) => upd(i, { _rows: [...(cases[i]._rows || []), { key: attrKeys[0] || '', value: '' }] });
  const setRow = (i, ri, patch) => upd(i, { _rows: cases[i]._rows.map((r, k) => (k === ri ? { ...r, ...patch } : r)) });
  const delRow = (i, ri) => upd(i, { _rows: cases[i]._rows.filter((_, k) => k !== ri) });

  const save = async () => {
    if (!loaded) { setError('Refresh before saving — the existing tests could not be loaded, so a save would overwrite them.'); return; }
    setBusy(true); setError(null);
    try { await setEvalCases(flowId, toApi(cases), authSession); setDirty(false); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true); setError(null); setResults(null);
    // eval-cases-load-error-overwrite: only auto-persist edits when the suite genuinely loaded — otherwise a
    // Run after a failed load (with a locally-added case) would REPLACE the server's real tests with just it.
    try { const api = toApi(cases); if (dirty && editable && loaded) { await setEvalCases(flowId, api, authSession); setDirty(false); } setResults(await runEval(flowId, api, authSession)); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const pass = (results || []).filter((r) => r.passed).length;
  // A labelled block: caption on its own line above the control, with breathing room (no overlap).
  const Grp = ({ label, hint, children }) => (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      {hint && <div className="text-[11px] text-slate-400 mb-1.5">{hint}</div>}
      {!hint && <div className="mb-1.5" />}
      {children}
    </div>
  );

  return (
    <div className="mt-6">
      <button className="flex items-center gap-2 text-sm font-semibold text-slate-700" onClick={() => setOpen((v) => !v)}>
        {open ? '▾' : '▸'} Tests (eval) {cases.length > 0 && <span className="text-[11px] font-normal text-slate-400">{cases.length} case{cases.length > 1 ? 's' : ''}</span>}
        {results && <Chip tone={pass === results.length ? 'green' : 'amber'}>{pass}/{results.length} pass</Chip>}
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500 mb-3">Scenarios + expected outcomes. "Run all" drives each through the chat <b>with actions firing</b> and checks the values, which fields fill, the branch it takes, and the actions that run.</div>
          {error && <Alert tone="danger" className="mb-2">{error}</Alert>}
          <div className="space-y-3">
            {cases.map((c, i) => {
              const r = (results || [])[i];
              return (
                <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <input className="rounded-md border border-slate-200 px-2.5 py-1.5 text-sm font-medium flex-1 min-w-0" value={c.name} onChange={(e) => upd(i, { name: e.target.value })} disabled={!editable} placeholder="Test name" />
                    {r && <Chip tone={r.passed ? 'green' : 'amber'}>{r.passed ? '✓ pass' : '✗ fail'}</Chip>}
                    {editable && <button className="text-rose-400 text-xs hover:text-rose-600 shrink-0" onClick={() => del(i)}>remove</button>}
                  </div>
                  <Grp label="Scenario" hint="What the customer wants — the AI plays this person through the chat.">
                    <textarea className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs" rows={2} value={c.scenario} onChange={(e) => upd(i, { scenario: e.target.value })} disabled={!editable} placeholder="e.g. One-way charter, 40 passengers, Peoria to Chicago, motorcoach, contact Jordan 309-555-0100" />
                  </Grp>

                  <Grp label="Expected values" hint="Attribute must equal this after the chat (numbers compared numerically).">
                    <div className="space-y-1.5">
                      {(c._rows || []).length === 0 && <div className="text-[11px] text-slate-300">No value checks.</div>}
                      {(c._rows || []).map((row, ri) => (
                        <div key={ri} className="flex gap-1.5 items-center">
                          <select className="rounded-md border border-slate-200 px-2 py-1.5 text-xs flex-1 min-w-0" value={row.key} onChange={(e) => setRow(i, ri, { key: e.target.value })} disabled={!editable}>
                            <option value="">— attribute —</option>
                            {attrKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                          </select>
                          <span className="text-slate-400 text-xs shrink-0">=</span>
                          <input className="rounded-md border border-slate-200 px-2 py-1.5 text-xs flex-1 min-w-0" value={row.value} onChange={(e) => setRow(i, ri, { value: e.target.value })} disabled={!editable} placeholder="expected value" />
                          {editable && <button className="text-rose-400 text-xs px-1 shrink-0 hover:text-rose-600" onClick={() => delRow(i, ri)}>✕</button>}
                        </div>
                      ))}
                      {editable && <button className="text-[11px] text-blue-600 hover:text-blue-800" onClick={() => addRow(i)}>+ add value check</button>}
                    </div>
                  </Grp>

                  <div className="grid md:grid-cols-3 gap-4">
                    <Grp label="Must complete">
                      <label className="text-xs text-slate-600 flex items-center gap-1.5 h-8"><input type="checkbox" checked={c.expectComplete !== false} onChange={(e) => upd(i, { expectComplete: e.target.checked })} disabled={!editable} /> flow reaches the end</label>
                    </Grp>
                    <Grp label="Expected branch">
                      <select className="rounded-md border border-slate-200 px-2 py-1.5 text-xs w-full" value={c.expectedBranch || ''} onChange={(e) => upd(i, { expectedBranch: e.target.value })} disabled={!editable}>
                        <option value="">— any —</option>
                        {branchOpts.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </Grp>
                    <Grp label="Final message contains">
                      <input className="rounded-md border border-slate-200 px-2 py-1.5 text-xs w-full" value={c.messageContains || ''} onChange={(e) => upd(i, { messageContains: e.target.value })} disabled={!editable} placeholder="e.g. 892" />
                    </Grp>
                  </div>

                  <Grp label="Actions that must run" hint="Hooks / code capabilities that should fire during this run.">
                    <ChipMulti options={actionOpts} value={c.expectedActions} onChange={(v) => upd(i, { expectedActions: v })} disabled={!editable} tone="green" />
                  </Grp>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Grp label="Fields that must be filled">
                      <ChipMulti options={attrKeys} value={c.expectFilled} onChange={(v) => upd(i, { expectFilled: v })} disabled={!editable} tone="green" />
                    </Grp>
                    <Grp label="Fields that must NOT be filled">
                      <ChipMulti options={attrKeys} value={c.expectNotFilled} onChange={(v) => upd(i, { expectNotFilled: v })} disabled={!editable} tone="rose" />
                    </Grp>
                  </div>

                  {r && !r.passed && <ul className="text-[11px] text-rose-600 list-disc pl-4 space-y-0.5">{(r.failures || []).map((f, k) => <li key={k}>{f}</li>)}</ul>}
                  {r && (
                    <div className="pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
                      <button className="hover:text-slate-700" onClick={() => setExpanded(expanded === i ? null : i)}>{expanded === i ? '▾ hide transcript' : '▸ transcript'}</button>
                      <span>· {r.status}</span>
                      {r.activeBranch && <span>· branch <span className="font-mono text-slate-700">{r.activeBranch}</span></span>}
                      {r.firedActions && Object.keys(r.firedActions).length > 0 && <span>· ran {Object.entries(r.firedActions).map(([n, ok]) => `${n}${ok ? '' : '✗'}`).join(', ')}</span>}
                    </div>
                  )}
                  {r && expanded === i && (
                    <div className="mt-1 rounded bg-slate-50 border border-slate-100 p-2 text-[11px] space-y-0.5 max-h-52 overflow-auto">
                      {(r.transcript || []).map((t, k) => <div key={k}><span className="text-slate-400">{t.role}:</span> {t.text}</div>)}
                    </div>
                  )}
                </div>
              );
            })}
            {cases.length === 0 && <div className="text-sm text-slate-400">No tests yet.</div>}
          </div>
          <div className="flex gap-2 mt-3">
            {editable && <Button size="sm" variant="outline" onClick={addCase}>+ Test</Button>}
            {editable && <Button size="sm" variant="ghost" onClick={save} disabled={busy || !dirty || !loaded}>{dirty ? 'Save tests' : 'Saved'}</Button>}
            <Button size="sm" onClick={run} disabled={busy || cases.length === 0}>{busy ? 'Running…' : 'Run all'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AiFlowTester({ flowId, authSession }) {
  const [open, setOpen] = useState(false);
  const [persona, setPersona] = useState(PERSONA_PRESETS[0]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const run = async () => {
    setBusy(true); setErr(null); setResult(null);
    try { setResult(await simulateFlow(flowId, persona, 12, authSession)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const tone = result?.status === 'COMPLETE' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : result?.status === 'ERROR' ? 'text-rose-700 bg-rose-50 border-rose-200' : 'text-amber-700 bg-amber-50 border-amber-200';
  return (
    <div className="mt-5">
      <button className="text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600" onClick={() => setOpen(!open)}>
        {open ? '▾' : '▸'} 🧪 Test with AI (simulate a customer)
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-slate-200 p-3">
          <div className="text-[11px] text-slate-500 mb-2">The AI plays a customer and talks to this flow — a dry run (no real actions fire). See if it completes or gets stuck before you publish.</div>
          <label className="block text-xs text-slate-500 mb-1">Customer persona</label>
          <textarea className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm mb-1" rows={2} value={persona} onChange={(e) => setPersona(e.target.value)} />
          <div className="flex flex-wrap gap-1 mb-2">
            {PERSONA_PRESETS.map((p) => <button key={p} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 hover:bg-slate-200" onClick={() => setPersona(p)}>{p.slice(0, 28)}…</button>)}
          </div>
          <Button size="sm" onClick={run} disabled={busy}>{busy ? 'Simulating…' : '▶ Run simulation'}</Button>
          {err && <div className="text-xs text-rose-500 mt-2">{err}</div>}
          {result && (
            <div className="mt-3">
              <div className={`text-sm rounded-md border p-2 mb-2 ${tone}`}>{result.verdict}</div>
              {Object.keys(result.captured || {}).length > 0 && (
                <div className="text-[11px] text-slate-500 mb-2">Captured: {Object.entries(result.captured).map(([k, v]) => `${k}=${v}`).join(' · ')}</div>
              )}
              <div className="space-y-1 max-h-72 overflow-auto">
                {(result.transcript || []).map((t, i) => (
                  <div key={i} className={`text-sm px-2 py-1 rounded ${t.role === 'customer' ? 'bg-blue-50 text-blue-800 ml-8' : t.role === 'error' ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-700 mr-8'}`}>
                    <b className="text-[10px] uppercase text-slate-400">{t.role === 'customer' ? 'customer (AI)' : t.role}</b> {t.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Per-flow public key + CORS allowlist. The embed-widget and runtime-API snippets used to live here too,
// but those now belong on the dedicated Integration & API page (OpsIntegration.js) — this keeps only the
// flow-specific bits: the resolved public key and which browser origins may present it.
function IntegrationSnippets({ flowId, flowName, authSession }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(null);
  const [origins, setOrigins] = useState('');
  const [corsMsg, setCorsMsg] = useState(null);
  const [err, setErr] = useState(null);
  // B5: sharing — the hosted chat-page link for this key, and its QR (for flyers / counters / vehicles).
  const url = key ? chatPageUrl(key, flowName) : null;
  const [qr, setQr] = useState(null);
  useEffect(() => {
    if (url) QRCode.toDataURL(url, { margin: 1, width: 168 }).then(setQr).catch(() => setQr(null));
    else setQr(null);
  }, [url]);
  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && !key) {
      try { const ek = await getFlowKey(flowId, authSession); setKey(ek?.publicKey); setOrigins((ek?.allowedOrigins || []).join('\n')); }
      catch (e) { setErr(e.message); }
    }
  };
  const saveCors = async () => {
    const list = origins.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    try { const ek = await setFlowCors(flowId, list, authSession); setOrigins((ek?.allowedOrigins || []).join('\n')); setCorsMsg('Saved'); setTimeout(() => setCorsMsg(null), 1500); }
    catch (e) { setErr(e.message); }
  };
  const k = key || 'mk_live_…';
  return (
    <div className="mt-5">
      <button className="text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600" onClick={toggle}>
        {open ? '▾' : '▸'} Public key & allowed origins
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {err && <div className="text-xs text-rose-500">{err}</div>}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-500">Public key (use in the widget / API on the Integration page)</span>
              <button className="text-[11px] text-blue-600" onClick={() => navigator.clipboard?.writeText(k)}>copy</button>
            </div>
            <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-lg p-2.5 overflow-auto whitespace-pre-wrap">{k}</pre>
          </div>
          {/* B5: share the flow as a real customer chat — hosted page link, website embed, QR. */}
          {key && (
            <div className="rounded-lg border border-slate-200 p-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Share this chat</div>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-slate-500">Hosted chat page — link it from email, SMS or a help center</span>
                      <button className="text-[11px] text-blue-600" onClick={() => navigator.clipboard?.writeText(url)}>copy link</button>
                    </div>
                    <a className="text-xs text-blue-600 break-all underline" href={url} target="_blank" rel="noreferrer">Open chat page ↗</a>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-slate-500">Website embed — paste before &lt;/body&gt;</span>
                      <button className="text-[11px] text-blue-600" onClick={() => navigator.clipboard?.writeText(embedSnippet(key))}>copy</button>
                    </div>
                    <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-lg p-2.5 overflow-auto whitespace-pre-wrap">{embedSnippet(key)}</pre>
                  </div>
                </div>
                {qr && (
                  <div className="shrink-0 text-center">
                    <img src={qr} alt="QR code linking to the chat page" className="rounded-md border border-slate-200" width={112} height={112} />
                    <div className="text-[10px] text-slate-400 mt-1">Scan to chat</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Per-flow CORS allowlist — restricts which sites may present this key from a browser. */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] text-slate-500">Allowed website origins (CORS) — one per line; empty = any site</span>
              {corsMsg && <span className="text-[11px] text-emerald-600">{corsMsg}</span>}
            </div>
            <textarea className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-mono" rows={2}
              value={origins} placeholder={'https://www.acme.com\nhttps://app.acme.com'} onChange={(e) => setOrigins(e.target.value)} />
            <div className="flex items-center gap-2 mt-1">
              <Button size="sm" variant="outline" onClick={saveCors}>Save allowed origins</Button>
              <span className="text-[11px] text-slate-400">Server-to-server calls (no Origin) are unaffected — the key is the auth.</span>
            </div>
          </div>
          <div className="text-[11px] text-slate-400">The public key resolves to exactly this tenant + flow server-side, <b>persists across restarts</b>, and <b>does not change when you publish</b> — so embeds and integrations keep working with zero downtime. It changes only if you explicitly rotate it (which deliberately invalidates the old key).</div>
        </div>
      )}
    </div>
  );
}

function VersionsPanel({ flowId, authSession, onRollback, busy }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [err, setErr] = useState(null);
  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && versions == null) {
      try { setVersions(await getVersions(flowId, authSession) || []); }
      catch (e) { setErr(e.message); setVersions([]); }
    }
  };
  useEffect(() => { setVersions(null); setOpen(false); setErr(null); }, [flowId]);
  return (
    <div className="mt-5">
      <button className="text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600" onClick={toggle}>
        {open ? '▾' : '▸'} Version history
      </button>
      {open && (
        <div className="mt-2 space-y-1">
          {err && <div className="text-xs text-rose-500">{err}</div>}
          {versions && versions.length === 0 && <div className="text-xs text-slate-400">No published versions yet.</div>}
          {(versions || []).map((v) => (
            <div key={v.version} className="flex items-center gap-2 text-sm text-slate-700">
              <Chip>v{v.version}</Chip>
              <span className="text-xs text-slate-400 flex-1 truncate">{v.publishedAt || v.createdAt || ''}</span>
              <button className="text-xs text-blue-600 disabled:opacity-40" disabled={busy} onClick={() => onRollback(v.version)}>Roll back</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mt-5 mb-2">{children}</div>;
}
function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 py-2">
      <div className="text-lg font-semibold text-slate-800">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function FlowTester({ flowId, authSession }) {
  const [messages, setMessages] = useState([]); // {who:'bot'|'me', text, components?:[]}
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [diag, setDiag] = useState(null); // latest session detail (debugger)
  const [aiTrace, setAiTrace] = useState([]); // every LLM call this conversation made (prompt/response)
  const [showDebug, setShowDebug] = useState(true);
  // B5 draft tester: test the WORKING DRAFT by default — that's what an author is iterating on. Untick
  // to test exactly what customers get (the published version).
  const [draftMode, setDraftMode] = useState(true);
  const esRef = useRef(null);
  const endRef = useRef(null);

  // After a turn settles, pull the session's persisted trace so the debugger shows what the engine did,
  // and the AI trace (exact prompt/response of each LLM call) so you can inspect/copy it while debugging.
  const refreshDiag = () => {
    if (!sessionId) return;
    getSession(sessionId, authSession).then(setDiag).catch(() => {});
    getAiTraceSession(sessionId, authSession).then((rows) => setAiTrace(Array.isArray(rows) ? rows : [])).catch(() => {});
  };

  // The <PayNow/> button: confirm payment → the engine resumes and completes; show the follow-up reply.
  const pay = async (props) => {
    if (!sessionId) return;
    try {
      const r = await confirmPayment(sessionId, props.attribute, props.ref);
      setMessages((m) => [...m, { who: 'bot', text: r.reply || 'Payment received.', components: r.richContent || [] }]);
      refreshDiag();
    } catch (e) { setError(e.message); }
  };

  // <FileUpload/> — the author's FILE attribute: upload the file (binding it to the attribute), show it in
  // the transcript (image inline), then submit the component result so the engine advances. Lets FILE-gated
  // flows (e.g. photo → AI vision → decision) be tested right here in Studio, per "test through flows".
  const upload = async (props, file) => {
    if (!sessionId || !file) return;
    try {
      const meta = await uploadSessionFile(sessionId, file, props.attr);
      const isImg = (meta.contentType || file.type || '').startsWith('image/');
      setMessages((m) => [...m, isImg
        ? { who: 'me', image: attachmentUrl(sessionId, meta.id), text: '' }
        : { who: 'me', text: `📎 ${meta.filename || file.name}` }]);
      const outputs = props.attr ? { [props.attr]: meta.filename || file.name } : {};
      const r = await submitComponentResult(sessionId, outputs);
      setMessages((m) => [...m, { who: 'bot', text: r.reply || '', components: r.richContent || [] }]);
      refreshDiag();
    } catch (e) { setError(e.status === 413 ? 'That file is too large to upload — try a smaller file.' : e.message); }
  };

  useEffect(() => () => { if (esRef.current) esRef.current.close(); }, []);
  // Keep the latest turn in view by scrolling the message list only (block:'nearest' — never the page).
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages]);

  const start = async () => {
    setStarting(true); setError(null); setMessages([]); setDiag(null);
    try {
      const key = (await getFlowKey(flowId, authSession))?.publicKey;
      const s = await startSession(key, undefined, { draft: draftMode, authSession });
      setSessionId(s.sessionId);
      if (s.greeting) setMessages([{ who: 'bot', text: s.greeting }]);
    } catch (e) { setError(e.message); }
    finally { setStarting(false); }
  };

  const send = () => {
    const text = input.trim();
    if (!text || !sessionId) return;
    setInput('');
    setMessages((m) => [...m, { who: 'me', text }, { who: 'bot', text: '', components: [], streaming: true }]);
    const es = new EventSource(streamUrl(sessionId, text));
    esRef.current = es;
    // Pure updaters (no mutation of prior-state objects) — required so React StrictMode's
    // double-invocation doesn't append each SSE chunk twice ("I'm I'm here here").
    const patchLast = (patch) => (m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, ...patch(msg) } : msg));
    es.addEventListener('message', (ev) => {
      setMessages(patchLast((msg) => ({ text: msg.text + ev.data })));
    });
    es.addEventListener('component', (ev) => {
      setMessages(patchLast((msg) => ({ components: [...(msg.components || []), ev.data] })));
    });
    es.addEventListener('done', () => { es.close(); setMessages(patchLast(() => ({ streaming: false }))); refreshDiag(); });
    es.addEventListener('error', () => {
      es.close();
      // tester-sse-error-swallowed: 'done' handles normal completion (it closes the stream first), so reaching
      // here means a genuine failure. Stop the spinner, keep any partial text but annotate an otherwise-empty
      // bubble so it doesn't read as "the flow replied with nothing", and surface the failure to the author.
      setMessages((m) => (m.length ? patchLast((msg) => ({ streaming: false, text: msg.text || '⚠ stream error — no reply received' }))(m) : m));
      setError('The tester stream ended unexpectedly (network or runtime error). Try again, or press New chat to restart.');
      refreshDiag();
    });
  };

  return (
    <div>
    {/* Bounded-height chat card: the message list scrolls INSIDE it and the input stays pinned at the
        bottom, so the latest reply and the input box are always visible together (no page scrolling). */}
    <div className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col"
         style={{ height: 'min(70vh, 560px)' }}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><IconBot size={18} /> Live tester</h3>
        <div className="flex gap-2 items-center">
          {/* B5 draft tester: on = the working draft (what you're editing); off = the published version. */}
          <label className="flex items-center gap-1 text-[11px] text-slate-500" title="Test the working draft (unpublished changes). Untick to test exactly what customers get.">
            <input type="checkbox" checked={draftMode} onChange={(e) => setDraftMode(e.target.checked)} />draft
          </label>
          <Button size="sm" variant={showDebug ? 'outlineIndigo' : 'ghost'} onClick={() => setShowDebug((d) => !d)}>Debug</Button>
          <Button size="sm" variant="outline" onClick={start} disabled={starting}>{sessionId ? 'New chat' : 'Start'}</Button>
        </div>
      </div>
      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}

      <div className="flex-1 overflow-auto mt-3 space-y-2 pr-1">
        {messages.length === 0 && <div className="text-sm text-slate-400">Press Start to begin a conversation.</div>}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.who === 'me' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.who === 'me' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
              <div className="flex items-center gap-1.5 text-[11px] opacity-70 mb-0.5">
                {m.who === 'me' ? <IconUser size={12} /> : <IconBot size={12} />}{m.who === 'me' ? 'You' : 'Assistant'}
              </div>
              {m.image
                ? <a href={m.image} target="_blank" rel="noreferrer"><img src={m.image} alt="shared" className="max-w-[200px] max-h-[200px] rounded-lg block" /></a>
                : <div className="whitespace-pre-wrap">{m.text}{m.streaming && <span className="animate-pulse">▍</span>}</div>}
              {(m.components || []).map((tag, j) => <ComponentCard key={j} tag={tag} onPay={pay} onUpload={upload} />)}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2 mt-3">
        <input
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
          placeholder={sessionId ? 'Type a message…' : 'Start a session first'}
          value={input} disabled={!sessionId}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
        />
        <Button size="sm" onClick={send} disabled={!sessionId}>Send</Button>
      </div>
    </div>
    {/* Debugger sits BELOW the chat card (its own page-scrollable block) so the trace never inflates the
        chat or pushes the input out of view. */}
    {showDebug && diag && <div className="mt-3"><DebugPanel detail={diag} /></div>}
    {showDebug && aiTrace.length > 0 && <div className="mt-3"><AiTracePanel calls={aiTrace} /></div>}
    </div>
  );
}

// Shows what the engine did on the latest turn — the tester's debugger. Reads the persisted session trace.
// Every LLM call the conversation made — model, tokens, latency, and the exact prompt/response, copyable
// so you can grab what went to/from the model while debugging an issue.
function AiTracePanel({ calls }) {
  const [open, setOpen] = useState(null);
  const [copied, setCopied] = useState(null);
  const copy = (text, key) => {
    try { navigator.clipboard.writeText(text || ''); setCopied(key); setTimeout(() => setCopied(null), 1200); } catch (e) { /* ignore */ }
  };
  const totIn = calls.reduce((s, c) => s + (c.inputTokens || 0), 0);
  const totOut = calls.reduce((s, c) => s + (c.outputTokens || 0), 0);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
        <IconSparkles size={16} /> AI trace
        <span className="text-[11px] font-normal text-slate-400">{calls.length} call{calls.length === 1 ? '' : 's'} · {totIn} in / {totOut} out tokens</span>
        <span className="flex-1" />
        <button className="text-[11px] text-indigo-500 hover:text-indigo-700" onClick={() => {
          try {
            const blob = new Blob([JSON.stringify(calls, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'ai-trace.json'; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (e) { /* ignore */ }
        }}>export</button>
      </div>
      <div className="space-y-1.5">
        {calls.map((c, i) => (
          <div key={c.id || i} className="border border-slate-100 rounded-lg">
            <button className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs" onClick={() => setOpen(open === i ? null : i)}>
              <span className="font-semibold text-indigo-600">{c.callType}</span>
              <span className="text-slate-400">{c.model}</span>
              <span className="text-slate-400">in {c.inputTokens} · out {c.outputTokens} tok</span>
              <span className="text-slate-400">{c.latencyMs}ms</span>
              <span className="flex-1" />
              <span className="text-slate-300">{open === i ? '▾' : '▸'}</span>
            </button>
            {open === i && (
              <div className="px-2.5 pb-2 space-y-2">
                <TraceBlock label="Prompt (sent to model)" text={c.prompt} copied={copied === `p${i}`} onCopy={() => copy(c.prompt, `p${i}`)} />
                <TraceBlock label="Response (from model)" text={c.response} copied={copied === `r${i}`} onCopy={() => copy(c.response, `r${i}`)} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TraceBlock({ label, text, copied, onCopy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[11px] text-slate-500">{label}</span>
        <button className="text-[11px] text-indigo-500 hover:text-indigo-700" onClick={onCopy}>{copied ? 'copied ✓' : 'copy'}</button>
      </div>
      <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-lg p-2 overflow-auto whitespace-pre-wrap max-h-64">{text || '(empty)'}</pre>
    </div>
  );
}

function DebugPanel({ detail }) {
  const s = detail.session || {};
  const turns = detail.turns || [];
  const last = turns[turns.length - 1] || {};
  const Row = ({ label, children }) => (
    <div className="flex gap-2 text-xs py-0.5"><span className="text-slate-400 w-24 shrink-0">{label}</span><span className="text-slate-700 flex-1 break-words">{children}</span></div>
  );
  const kv = (o) => Object.entries(o || {}).map(([k, v]) => `${k}=${v}`).join(', ') || '—';
  return (
    <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Debugger · turn {last.turnIndex ?? '—'}</div>
        <div className="text-[11px] text-slate-400">{s.status} · {s.tokensUsed} tokens · {last.latencyMs ?? 0}ms</div>
      </div>
      <Row label="extracted">{kv(last.extracted)}</Row>
      <Row label="resolved">{kv(s.values)}</Row>
      <Row label="validation">{Object.keys(last.errors || {}).length ? kv(last.errors) : <span className="text-emerald-600">all valid</span>}</Row>
      <Row label="hooks">{(last.hooks || []).join(', ') || '—'}</Row>
      <Row label="branch">{last.branchId || '—'}</Row>
      <Row label="missing">{(last.missing || []).join(', ') || '—'}</Row>
      {/* B5: what each action actually sent and got back this turn — rendered request + response. */}
      {(last.hookDebug || []).length > 0 && (
        <div className="mt-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-400 mb-1">Action request / response</div>
          <div className="space-y-1">
            {last.hookDebug.map((d, i) => (
              <pre key={i} className="text-[11px] bg-white border border-indigo-100 rounded-lg p-2 overflow-auto whitespace-pre-wrap max-h-48">{d}</pre>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// A hook transformer emits <Component .../> tags; render a styled card (a real portal maps these to components).
function ComponentCard({ tag, onPay, onUpload }) {
  const m = /^<\s*([A-Za-z][\w-]*)([^>]*)\/?>$/.exec(String(tag).trim());
  if (!m) return null;
  const name = m[1];
  const props = {};
  const re = /([\w-]+)\s*=\s*"([^"]*)"/g; let a;
  while ((a = re.exec(m[2]))) props[a[1]] = a[2];

  // <FileUpload/> — a FILE attribute: render a real picker so authors can test the upload gate in Studio.
  if (name === 'FileUpload') {
    return (
      <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
        <div className="text-xs text-blue-700 font-semibold mb-1">📎 Attach a file{props.attr ? ` (${props.attr})` : ''}</div>
        <input type="file" accept={props.accept || 'image/*,application/pdf'} className="text-xs"
          onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f && onUpload) onUpload(props, f); e.target.value = ''; }} />
      </div>
    );
  }

  // <PayNow/> renders as a real "Pay now" call-to-action: the button confirms payment and the session
  // resumes to completion. A real embed would open props.url (Stripe/hosted page) first, then confirm.
  if (name === 'PayNow') {
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <div className="text-xs text-emerald-700 font-semibold mb-1">Payment</div>
        <div className="text-sm text-slate-700 mb-2">Amount due: <b>{props.amount ? `$${props.amount}` : '—'}</b>{props.ref && <span className="text-slate-400"> · {props.ref}</span>}</div>
        <button onClick={() => onPay && onPay(props)}
          className="w-full rounded-lg bg-emerald-600 text-white text-sm font-medium py-2 hover:bg-emerald-700">Pay now</button>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-lg border-l-4 border-blue-500 bg-white text-slate-800 p-2">
      <div className="font-semibold text-xs mb-1">{name}</div>
      {Object.entries(props).map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 text-xs"><span className="text-slate-400">{k}</span><b>{v}</b></div>
      ))}
    </div>
  );
}

export default OpsFlows;
